/**
 * Workspaces Service
 * Handles workspace (organization) CRUD and membership. Personal account = no workspace (null).
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { Workspace, WorkspaceMember } from "@shared/schema";

export type WorkspaceRole = "owner" | "admin" | "member";

export interface InsertWorkspace {
  name: string;
  slug?: string | null;
}

export interface WorkspaceWithRole extends Workspace {
  role: WorkspaceRole;
  isPersonal?: boolean;
}

class WorkspacesService {
  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  private mapToWorkspace(row: any): Workspace {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug ?? null,
      createdByUserId: row.created_by_user_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  private mapToWorkspaceMember(row: any): WorkspaceMember & { role: WorkspaceRole } {
    return {
      id: row.id,
      workspaceId: row.workspace_id,
      userId: row.user_id,
      role: (row.role || "member") as WorkspaceRole,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  /** Get workspaces the current user belongs to (created or member). */
  async getWorkspaces(): Promise<WorkspaceWithRole[]> {
    const userId = await this.getCurrentUserId();

    const { data: created, error: createdError } = await supabase
      .from("workspaces")
      .select("*")
      .eq("created_by_user_id", userId)
      .order("updated_at", { ascending: false });

    if (createdError) throw new Error(`Failed to fetch workspaces: ${createdError.message}`);

    const { data: memberRows, error: memberError } = await supabase
      .from("workspace_members")
      .select("workspace_id, role")
      .eq("user_id", userId);

    if (memberError) throw new Error(`Failed to fetch workspace memberships: ${memberError.message}`);

    const memberWorkspaceIds = new Set((memberRows || []).map((r: any) => r.workspace_id));
    const memberRoleByWorkspaceId: Record<string, WorkspaceRole> = {};
    (memberRows || []).forEach((r: any) => {
      memberRoleByWorkspaceId[r.workspace_id] = (r.role || "member") as WorkspaceRole;
    });

    const result: WorkspaceWithRole[] = (created || []).map((row: any) => ({
      ...this.mapToWorkspace(row),
      role: row.created_by_user_id === userId ? "owner" : (memberRoleByWorkspaceId[row.id] || "member"),
      isPersonal: false,
    }));

    const idsFetched = new Set(result.map((w) => w.id));
    const toFetch = (memberRows || [])
      .map((r: any) => r.workspace_id)
      .filter((id: string) => !idsFetched.has(id));
    if (toFetch.length > 0) {
      const { data: memberWorkspaces, error: fetchErr } = await supabase
        .from("workspaces")
        .select("*")
        .in("id", toFetch)
        .order("updated_at", { ascending: false });
      if (!fetchErr && memberWorkspaces) {
        memberWorkspaces.forEach((row: any) => {
          result.push({
            ...this.mapToWorkspace(row),
            role: (memberRoleByWorkspaceId[row.id] || "member") as WorkspaceRole,
            isPersonal: false,
          });
        });
      }
    }

    return result;
  }

  /** Get a single workspace by id; requires membership or creator. */
  async getWorkspace(id: string): Promise<WorkspaceWithRole | null> {
    const userId = await this.getCurrentUserId();

    const { data: w, error } = await supabase.from("workspaces").select("*").eq("id", id).single();
    if (error || !w) return null;

    if (w.created_by_user_id === userId) {
      return { ...this.mapToWorkspace(w), role: "owner", isPersonal: false };
    }

    const { data: member } = await supabase
      .from("workspace_members")
      .select("role")
      .eq("workspace_id", id)
      .eq("user_id", userId)
      .maybeSingle();

    if (!member) return null;
    return { ...this.mapToWorkspace(w), role: (member.role || "member") as WorkspaceRole, isPersonal: false };
  }

  /** Create a workspace and add the current user as owner. */
  async createWorkspace(params: InsertWorkspace): Promise<WorkspaceWithRole> {
    const userId = await this.getCurrentUserId();
    const slug =
      params.slug?.trim() ||
      params.name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

    const { data: workspace, error: insertError } = await supabase
      .from("workspaces")
      .insert({
        name: params.name.trim(),
        slug: slug || null,
        created_by_user_id: userId,
      })
      .select()
      .single();

    if (insertError) throw new Error(`Failed to create workspace: ${insertError.message}`);
    if (!workspace) throw new Error("Failed to create workspace: no data returned");

    const { error: memberError } = await supabase.from("workspace_members").insert({
      workspace_id: workspace.id,
      user_id: userId,
      role: "owner",
    });

    if (memberError) {
      await supabase.from("workspaces").delete().eq("id", workspace.id);
      throw new Error(`Failed to add you as workspace owner: ${memberError.message}`);
    }

    return { ...this.mapToWorkspace(workspace), role: "owner", isPersonal: false };
  }

  /** Update workspace (creator or admin). */
  async updateWorkspace(id: string, updates: { name?: string; slug?: string | null }): Promise<Workspace | null> {
    const userId = await this.getCurrentUserId();
    const ws = await this.getWorkspace(id);
    if (!ws || (ws.role !== "owner" && ws.role !== "admin")) return null;

    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) payload.name = updates.name.trim();
    if (updates.slug !== undefined) payload.slug = updates.slug?.trim() || null;

    const { data, error } = await supabase
      .from("workspaces")
      .update(payload)
      .eq("id", id)
      .select()
      .single();

    if (error) throw new Error(`Failed to update workspace: ${error.message}`);
    return data ? this.mapToWorkspace(data) : null;
  }

  /** Delete workspace (creator only). */
  async deleteWorkspace(id: string): Promise<boolean> {
    const ws = await this.getWorkspace(id);
    if (!ws || ws.role !== "owner") return false;

    const { error } = await supabase.from("workspaces").delete().eq("id", id);
    if (error) throw new Error(`Failed to delete workspace: ${error.message}`);
    return true;
  }

  /** List members of a workspace. */
  async getWorkspaceMembers(workspaceId: string): Promise<(WorkspaceMember & { role: WorkspaceRole })[]> {
    const ws = await this.getWorkspace(workspaceId);
    if (!ws) return [];

    const { data, error } = await supabase
      .from("workspace_members")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: true });

    if (error) throw new Error(`Failed to list members: ${error.message}`);
    return (data || []).map((row: any) => this.mapToWorkspaceMember(row));
  }

  /** Add a member (by user id). Creator or admin only. */
  async addMember(workspaceId: string, userId: string, role: WorkspaceRole = "member"): Promise<void> {
    const ws = await this.getWorkspace(workspaceId);
    if (!ws || (ws.role !== "owner" && ws.role !== "admin")) {
      throw new Error("You do not have permission to add members");
    }
    if (role === "owner") throw new Error("Cannot add another owner");

    const { error } = await supabase.from("workspace_members").insert({
      workspace_id: workspaceId,
      user_id: userId,
      role,
    });

    if (error) throw new Error(`Failed to add member: ${error.message}`);
  }

  /** Remove a member (or leave). Admins can remove others; any member can leave. */
  async removeMember(workspaceId: string, userId: string): Promise<void> {
    const currentUserId = await this.getCurrentUserId();
    const ws = await this.getWorkspace(workspaceId);
    if (!ws) throw new Error("Workspace not found");

    if (userId === currentUserId) {
      const { error } = await supabase
        .from("workspace_members")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("user_id", currentUserId);
      if (error) throw new Error(`Failed to leave workspace: ${error.message}`);
      return;
    }

    if (ws.role !== "owner" && ws.role !== "admin") {
      throw new Error("You do not have permission to remove members");
    }

    const { error } = await supabase
      .from("workspace_members")
      .delete()
      .eq("workspace_id", workspaceId)
      .eq("user_id", userId);
    if (error) throw new Error(`Failed to remove member: ${error.message}`);
  }

  /** Check if current user has access to a workspace (creator or member). */
  async hasAccess(workspaceId: string): Promise<boolean> {
    const ws = await this.getWorkspace(workspaceId);
    return ws !== null;
  }
}

export const workspacesService = new WorkspacesService();
